// Bridges between the rich iReal-style chart model and the app's existing
// Song / SequenceChord models.
//
// The IRealChart is the Workbench's lead-sheet projection; the optimiser and
// diagram renderer consume a flat SequenceChord[] derived from the chart's
// performance order (repeats/endings expanded). These converters keep music/
// pure (no React).

import { melodyFromTicks, melodyToTicks, transposeMelody } from './chartMelody';
import { chordSymbol, type ChordTypeId } from './chords';
import {
  accidentalForKey,
  conventionalTonicName,
  parseKey,
  pitchClassOf,
  sharpName,
  spellNote,
} from './notes';
import { flattenChart, flattenMeasures } from './ireal/flatten';
import type { IRealChart, IRealChord, IRealChordRef, IRealMeasure } from './ireal/types';
import { parenthesizeChordSymbol, prettyChordSymbol } from './ireal/chordParser';
import {
  type Bar,
  type ChordEvent,
  type Section,
  type SequenceChord,
  type Song,
  uid,
} from './song';
import type { StringSet } from './tuning';

function prettyRoot(note: string): string {
  return note.replace(/b/g, '♭').replace(/#/g, '♯');
}

/** Harmony imported from iReal is normally beat-quantised, while Score
 * Studio also authors subdivisions (for example a half-beat N.C. pickup).
 * Preserve every positive written span and repair only malformed values. */
function harmonySpan(beats: number): number {
  return Number.isFinite(beats) && beats > 0 ? beats : 1;
}

/**
 * Display symbol for a chart chord: catalogue chords get the app's typeset
 * symbol (e.g. "Cm(maj7)"), imported chords keep their iReal text (e.g. "C13").
 */
export function chartChordSymbol(ref: IRealChordRef): string {
  if (ref.chordType && IREAL_QUALITY_BY_TYPE[ref.chordType] === ref.quality) {
    // Pretty accidentals on the root too ("B♭maj7"), or a page mixes B♭
    // (via the quality-differs path) with raw Bb6 — the ChordSymbol
    // typesetter parses either form.
    const base = chordSymbol(prettyRoot(ref.root), ref.chordType);
    return parenthesizeChordSymbol(ref.bass ? `${base}/${prettyRoot(ref.bass)}` : base, ref.parentheses);
  }
  return prettyChordSymbol(ref);
}

/**
 * Display symbol for the notation engraver: what the SOURCE wrote — a bare
 * "Am" playing as min7, an "A13" playing as dom13, a slash bass — falling
 * back to the catalogue label for plain catalogue chords.
 */
export function displayChordSymbol(ref: IRealChordRef): string {
  return chartChordSymbol(ref);
}

/** iReal quality string for each catalogue type (chosen so it round-trips). */
export const IREAL_QUALITY_BY_TYPE: Record<ChordTypeId, string> = {
  maj7: '^7',
  maj6: '6',
  maj7s5: '^7#5',
  maj7b5: '^7b5',
  maj9: '^9',
  // iReal's own spelling: a `/` in a chord token reads as a slash bass.
  maj69: '69',
  dom7: '7',
  dom7sus4: '7sus',
  dom7s5: '7#5',
  dom7b5: '7b5',
  dom9: '9',
  dom13: '13',
  dom7b9: '7b9',
  dom7s9: '7#9',
  min6: '-6',
  min7: '-7',
  min7b5: '-7b5',
  min7s5: '-7#5',
  minmaj7: '-^7',
  min9: '-9',
  min7b5b9: '-7b5b9',
  min7b59: 'h9',
  min69: '-69',
  dim7: 'o7',
  dimmaj7: 'o^7',
  // The triad tier prints BARE, the way a lead sheet writes a triad — and
  // these are exactly iReal's own triad qualities, so a written triad
  // round-trips through an export rather than being promoted to a seventh.
  maj: '',
  min: '-',
  aug: '+',
  dim: 'o',
};

/** Build an iReal chord reference for a catalogue chord. */
export function refFromType(root: string, chordType: ChordTypeId, bass?: string): IRealChordRef {
  return { root, quality: IREAL_QUALITY_BY_TYPE[chordType], chordType, ...(bass ? { bass } : {}) };
}

/** Build a full IRealChord for a manually entered catalogue chord. */
export function chordFromType(
  root: string,
  chordType: ChordTypeId,
  beats: number,
  extras: {
    bass?: string;
    quality?: string;
    parentheses?: IRealChordRef['parentheses'];
    targetTopNote?: string;
    preferredInversion?: number;
  } = {},
): IRealChord {
  const rawRef =
    'quality' in extras && extras.quality !== undefined
      ? {
          root,
          quality: extras.quality,
          chordType,
          ...(extras.bass ? { bass: extras.bass } : {}),
        }
      : refFromType(root, chordType, extras.bass);
  const ref = { ...rawRef, ...(extras.parentheses ? { parentheses: extras.parentheses } : {}) };
  return {
    id: uid('c'),
    ...ref,
    beats: Math.max(1, beats),
    symbol: chartChordSymbol(ref),
    ...(extras.targetTopNote ? { targetTopNote: extras.targetTopNote } : {}),
    ...(Number.isInteger(extras.preferredInversion)
      ? { preferredInversion: extras.preferredInversion }
      : {}),
  };
}

/**
 * Transpose every chord (and the key) by a number of semitones. Pass an explicit
 * `targetKey` to control the destination key's spelling (so chords follow the
 * chosen key's accidental); otherwise it's derived from the current key.
 */
export function transposeChart(
  chart: IRealChart,
  semitones: number,
  targetKey?: string,
): IRealChart {
  const shift = ((semitones % 12) + 12) % 12;
  const keyCtx = parseKey(chart.key);
  let newKey: string | undefined;
  if (targetKey) {
    newKey = targetKey;
  } else {
    // Spell the DESTINATION tonic by its own convention, not the source key's
    // accidental — C major +3 is Eb, never "D#" (which isn't a key at all and
    // would drag every chord into sharp spelling).
    const newKeyTonic = keyCtx
      ? conventionalTonicName((pitchClassOf(keyCtx.tonic) + shift) % 12, keyCtx.mode)
      : undefined;
    newKey =
      keyCtx && newKeyTonic
        ? keyCtx.mode === 'minor'
          ? `${newKeyTonic} minor`
          : newKeyTonic
        : chart.key;
  }
  if (shift === 0 && newKey === chart.key) return chart;
  const destKey = parseKey(newKey ?? chart.key);
  const acc = accidentalForKey(destKey);

  const shiftNote = (note: string) => spellNote((pitchClassOf(note) + shift) % 12, acc);
  const shiftRef = <T extends IRealChordRef>(ref: T): T => {
    const root = shiftNote(ref.root);
    const bass = ref.bass ? shiftNote(ref.bass) : undefined;
    return { ...ref, root, ...(bass ? { bass } : { bass: undefined }) };
  };

  return {
    ...chart,
    key: newKey,
    measures: chart.measures.map((m) => ({
      ...m,
      ...(m.melody ? { melody: transposeMelody(m.melody, shift, destKey) } : {}),
      chords: m.chords.map((c) => {
        if (c.noChord) return c;
        const moved = shiftRef(c);
        const alternate = c.alternate ? shiftRef(c.alternate) : c.alternate;
        const targetTopNote = c.targetTopNote ? shiftNote(c.targetTopNote) : undefined;
        return {
          ...moved,
          alternate,
          targetTopNote,
          symbol: chartChordSymbol(moved),
        };
      }),
    })),
  };
}

export function createEmptyChart(title = 'Untitled Chart', key = 'C'): IRealChart {
  return {
    title,
    key,
    timeSignature: [4, 4],
    measures: [{ id: uid('m'), chords: [], timeSig: [4, 4], close: 'final' }],
  };
}

/** Convert a Song preset into an iReal-style chart. */
export function songToChart(song: Song): IRealChart {
  const measures: IRealMeasure[] = [];
  song.sections.forEach((section, si) => {
    section.bars.forEach((bar, bi) => {
      const sorted = [...bar.chords].sort((a, b) => (a.beat || 1) - (b.beat || 1));
      const chords: IRealChord[] = sorted.map((e) =>
        chordFromType(e.root, e.chordType, e.duration || bar.beats || 4, {
          targetTopNote: e.targetTopNote,
          preferredInversion: e.preferredInversion,
        }),
      );
      const measure: IRealMeasure = { id: uid('m'), chords };
      if (bar.melody && bar.melody.length > 0) measure.melody = melodyToTicks(bar.melody);
      if (si === 0 && bi === 0) {
        measure.timeSig = song.timeSignature;
        if (bar.beats > 0 && bar.beats < song.timeSignature[0]) measure.pickupBeats = bar.beats;
      }
      if (bi === 0 && section.name) measure.section = section.name;
      if (bi === 0 && si > 0) measure.open = 'double';
      measures.push(measure);
    });
  });
  if (measures.length === 0)
    measures.push({ id: uid('m'), chords: [], timeSig: song.timeSignature });
  measures[0].timeSig = measures[0].timeSig ?? song.timeSignature;
  measures[measures.length - 1].close = 'final';
  return {
    title: song.title,
    key: song.key,
    timeSignature: song.timeSignature,
    measures,
  };
}

/**
 * Convert a chart into the Song model the notation engine renders — the
 * inverse of songToChart. Measures become bars (beat positions accumulated
 * from chord spans, melody ticks converted back to fractional beats); a
 * measure carrying a `section` mark starts a new Song section. Unmapped
 * qualities are dropped; explicit noChord spans stay in Bar.harmonyLabels for
 * engraving but never enter the playable Bar.chords lane. Structure (repeats,
 * voltas, codas) is NOT expanded — pass
 * a flattened chart (`{...chart, measures: flattenChart(chart)}`) when the
 * performance order is wanted rather than the written form.
 */
export function chartToSong(chart: IRealChart): Song {
  const sections: Section[] = [];
  let meter: [number, number] = chart.timeSignature;
  chart.measures.forEach((m, i) => {
    if (m.timeSig) meter = m.timeSig;
    if (sections.length === 0 || m.section) {
      sections.push({
        id: `s${sections.length}`,
        name: m.section ?? (i === 0 ? 'Main' : ''),
        bars: [],
      });
    }
    const chords: ChordEvent[] = [];
    const harmonyLabels: NonNullable<Bar['harmonyLabels']> = [];
    let beat = 1;
    for (const c of m.chords) {
      const span = harmonySpan(c.beats);
      if (c.noChord) {
        harmonyLabels.push({ sourceId: c.id, beat, duration: span, text: c.symbol || 'N.C.' });
      } else if (c.chordType) {
        chords.push({
          sourceId: c.id,
          beat,
          duration: span,
          root: c.root,
          chordType: c.chordType,
          symbol: displayChordSymbol(c),
          ...(c.targetTopNote ? { targetTopNote: c.targetTopNote } : {}),
          ...(Number.isInteger(c.preferredInversion)
            ? { preferredInversion: c.preferredInversion }
            : {}),
          ...(c.bass ? { bass: c.bass } : {}),
        });
      }
      beat += span;
    }
    const bar: Bar = {
      id: m.id,
      beats: m.pickupBeats ?? meter[0],
      chords,
      ...(harmonyLabels.length ? { harmonyLabels } : {}),
    };
    const [top, bottom] = chart.timeSignature;
    if (meter[0] !== top || meter[1] !== bottom) bar.timeSig = meter;
    if (m.melody && m.melody.length > 0) bar.melody = melodyFromTicks(m.melody);
    sections[sections.length - 1].bars.push(bar);
  });
  if (sections.length === 0) sections.push({ id: 's0', name: 'Main', bars: [] });
  return {
    id: chart.id ?? uid('song'),
    title: chart.title,
    key: chart.key,
    timeSignature: chart.timeSignature,
    sections,
  };
}

/** Group a flat SequenceChord[] (by barIndex) back into chart measures. */
export function sequenceToChart(
  chart: SequenceChord[],
  title: string,
  key: string,
  timeSignature: [number, number] = [4, 4],
): IRealChart {
  const maxBar = chart.reduce((m, c) => Math.max(m, c.barIndex), -1);
  const measures: IRealMeasure[] = [];
  for (let b = 0; b <= maxBar; b++) {
    const inBar = chart.filter((c) => c.barIndex === b).sort((a, b2) => a.beat - b2.beat);
    const chords: IRealChord[] = inBar.map((c) =>
      chordFromType(c.displayRoot, c.chordType, c.durationBeats, {
        targetTopNote: c.targetTopNote,
        preferredInversion: c.preferredInversion,
      }),
    );
    measures.push({ id: uid('m'), chords });
  }
  if (measures.length === 0) measures.push({ id: uid('m'), chords: [] });
  measures[0].timeSig = timeSignature;
  measures[measures.length - 1].close = 'final';
  return { title, key, timeSignature, measures };
}

export interface ChartToSequenceOptions {
  stringSet: StringSet;
  /** Repeat the whole expanded form this many times. */
  wholeRepeats?: number;
}

/**
 * Flatten a chart into the beat-positioned SequenceChord[] the optimiser and
 * diagram renderer consume. No-chord cells are skipped (they carry no voicing).
 */
export function chartToSequence(
  chart: IRealChart,
  options: ChartToSequenceOptions,
): SequenceChord[] {
  const flat = flattenChart(chart, { wholeRepeats: options.wholeRepeats });
  return flatMeasuresToSequence(flat, options.stringSet, chart.timeSignature);
}

/**
 * Convert an already-expanded performance form without interpreting it again.
 *
 * Harmony symbols remain in force until the next explicit symbol. Extending
 * the emitted duration here gives every playback consumer the same timeline:
 * comping and walking bass continue through otherwise-empty measures, while
 * leading empty measures stay silent. An explicit N.C. (or an unplayable
 * imported symbol) clears the running harmony instead of being treated as an
 * implicit continuation.
 */
export function flatMeasuresToSequence(
  flat: IRealMeasure[],
  stringSet: StringSet,
  defaultTimeSignature: [number, number],
): SequenceChord[] {
  const out: SequenceChord[] = [];
  let meter = defaultTimeSignature;
  let measureStart = 0;
  let active: SequenceChord | null = null;
  let activeStart = 0;

  const extendActiveTo = (boundary: number) => {
    if (!active) return;
    active.durationBeats = Math.max(active.durationBeats, boundary - activeStart);
  };

  flat.forEach((measure, barIndex) => {
    meter = measure.timeSig ?? meter;
    let beat = 1;
    measure.chords.forEach((chord, ci) => {
      const span = harmonySpan(chord.beats);
      const absoluteStart = measureStart + (beat - 1);
      // Any written harmony mark is a boundary for the preceding chord. Empty
      // space between marks is implicit continuation; a written N.C. is not.
      extendActiveTo(absoluteStart);
      if (chord.chordType && !chord.noChord) {
        const event: SequenceChord = {
          // barIndex (flat position) keeps ids unique across repeated passes.
          id: `${measure.id}-${barIndex}-${ci}`,
          // The WRITTEN bar (repeated passes share it) — the pin key's anchor.
          barId: measure.id,
          root: sharpName(pitchClassOf(chord.root)),
          displayRoot: chord.root,
          chordType: chord.chordType,
          symbol: chord.symbol || chordSymbol(chord.root, chord.chordType),
          barIndex,
          beat,
          durationBeats: span,
          writtenBeats: span,
          stringSet,
          targetTopNote: chord.targetTopNote,
          preferredInversion: Number.isInteger(chord.preferredInversion)
            ? chord.preferredInversion
            : undefined,
          // A slash bass is part of the chord, not part of its name: it
          // travels so the instruments that play the bottom can honour it.
          ...(chord.bass ? { bass: chord.bass } : {}),
        };
        out.push(event);
        active = event;
        activeStart = absoluteStart;
      } else {
        active = null;
      }
      beat += span;
    });
    measureStart += measure.pickupBeats ?? meter[0];
  });
  // A final chord remains active through trailing blank measures, but never
  // synthesise harmony backwards into blank bars at the start of the form.
  extendActiveTo(measureStart);
  return out;
}

/**
 * The AUTHORED chord index a sequence entry realises — the `ci` of the
 * `measureId-barIndex-ci` id `flatMeasuresToSequence` mints, counting every
 * chord the measure writes (an N.C. or an unplayable symbol included), which
 * is exactly what a surface drawing `measure.chords` counts. A sheet that
 * draws only the VOICED chords must key by this, never by its own position:
 * one N.C. ahead of a chord shifts every later position off its chord. Null
 * for an id this module did not mint.
 */
export function sequenceChordIndex(id: string): number | null {
  // Parse from the right: the measure id itself may contain hyphens, the two
  // numeric tails never do.
  const tail = /-\d+-(\d+)$/.exec(id);
  return tail ? Number(tail[1]) : null;
}

/**
 * Index a flattened-and-voiced sequence back onto the AUTHORED chords — the
 * inverse of the id `flatMeasuresToSequence` mints (`measureId-barIndex-ci`,
 * where `ci` indexes the measure's raw chord list). This is what lets a
 * surface that renders the authored form once (the notation pane's chord
 * symbols) play the same voicing the diagram sheet shows: look the pressed
 * chord's id up here.
 *
 * Repeated bars share a measure id across passes; the FIRST pass wins, which
 * is exactly the choice the diagram sheet already makes. Lives beside the id
 * format because the two must never drift apart.
 */
export function firstPassVoicingsByChordId<T extends { id: string; barIndex: number }>(
  chart: IRealChart,
  sequence: readonly T[] | null | undefined,
): Map<string, T> {
  const best = new Map<string, T>();
  for (const entry of sequence ?? []) {
    // Parse from the right: the measure id itself contains hyphens, the two
    // numeric tails never do.
    const parts = entry.id.split('-');
    const ci = Number(parts[parts.length - 1]);
    const barIndex = Number(parts[parts.length - 2]);
    if (!Number.isInteger(ci) || !Number.isInteger(barIndex)) continue;
    const key = `${parts.slice(0, -2).join('-')}::${ci}`;
    const prev = best.get(key);
    if (!prev || entry.barIndex < prev.barIndex) best.set(key, entry);
  }
  const out = new Map<string, T>();
  for (const measure of chart.measures) {
    measure.chords.forEach((chord, ci) => {
      const hit = best.get(`${measure.id}::${ci}`);
      if (hit) out.set(chord.id, hit);
    });
  }
  return out;
}

/**
 * Expand a chart to its full played length for a linear "unrolled" view: repeats
 * and endings are written out (no voltas / repeat signs), each pass gets a unique
 * measure id, and a section that recurs is primed — A, A′, A″ — so the second time
 * through reads as its own pass. Voice leading already carries across the seam
 * because the optimiser runs on this same flattened order (see `chartToSequence`),
 * so an unrolled A′ can (and often will) voice differently from A. The measure
 * order matches `chartToSequence`'s `barIndex`, so callers can map voicings by index.
 *
 * `wholeRepeats` writes the *entire* expanded form out that many times (the
 * form-cycle view): the section priming continues across passes, so a one-section
 * tune reads A → A′ → A″ and the measure order still matches
 * `chartToSequence(chart, { wholeRepeats })`.
 */
export function unrollChart(
  chart: IRealChart,
  options: { wholeRepeats?: number; pickupOnce?: boolean } = {},
): IRealChart {
  const once = flattenMeasures(chart.measures);
  const reps = Math.max(1, options.wholeRepeats ?? 1);
  const flat: IRealMeasure[] = [];
  const passStarts = new Set<number>();
  for (let r = 0; r < reps; r++) {
    passStarts.add(flat.length);
    if (options.pickupOnce && r > 0 && once[0]?.pickupBeats != null) {
      const body = once.slice(1);
      if (body.length > 0) {
        flat.push({
          ...body[0],
          timeSig: body[0].timeSig ?? once[0].timeSig ?? chart.timeSignature,
        });
        flat.push(...body.slice(1));
      }
    } else {
      flat.push(...once);
    }
  }
  // A sectionless tune written out over several passes needs *something* to mark
  // where each pass begins — stamp its opening bar 'A' so priming yields A → A′.
  const stampPassStarts = reps > 1 && once.length > 0 && once[0].section == null;
  const sectionCount = new Map<string, number>();
  let runningMeter: [number, number] | undefined;
  const measures: IRealMeasure[] = flat.map((m, i) => {
    const section = m.section ?? (stampPassStarts && passStarts.has(i) ? 'A' : undefined);
    const next: IRealMeasure = {
      ...m,
      id: `${m.id}#${i}`, // unique per pass so each expansion is distinct
      // Linear form: drop repeat/volta/simile chrome and the imported grid cells
      // (so computeLayout packs the expanded bars left-to-right instead of
      // honouring the now-repeating 16-cell positions).
      open: m.open === 'repeat' ? undefined : m.open,
      close: undefined,
      ending: undefined,
      barRepeat: undefined,
      cell: undefined,
      cells: undefined,
    };
    // Keep the time signature only where it's the initial meter or a genuine
    // change — a repeated section carries the same `timeSig` on its first bar,
    // which would re-print "4/4" at the top of A′ (a duplicate of the leading one).
    if (m.timeSig) {
      const changed =
        !runningMeter || m.timeSig[0] !== runningMeter[0] || m.timeSig[1] !== runningMeter[1];
      runningMeter = m.timeSig;
      if (!changed) next.timeSig = undefined;
    }
    if (section != null) {
      const seen = sectionCount.get(section) ?? 0;
      sectionCount.set(section, seen + 1);
      next.section = seen === 0 ? section : `${section}${'′'.repeat(seen)}`;
    }
    return next;
  });
  if (measures.length > 0) {
    measures[measures.length - 1] = { ...measures[measures.length - 1], close: 'final' };
  }
  return { ...chart, measures };
}

/** Display string for a chord ref (used by chart UI). */
export function refSymbol(ref: IRealChordRef): string {
  return prettyChordSymbol(ref);
}

/**
 * Absolute beat at which each measure begins, honouring per-measure time
 * signatures (a measure's beat count is its numerator). Drives the playhead and
 * the playback timeline so mixed-meter charts stay in time.
 */
export function measureStartBeats(
  measures: Pick<IRealMeasure, 'timeSig' | 'pickupBeats'>[],
  defaultTimeSig: [number, number],
): number[] {
  const out: number[] = [];
  let acc = 0;
  let meter = defaultTimeSig;
  for (const m of measures) {
    meter = m.timeSig ?? meter;
    out.push(acc);
    acc += m.pickupBeats ?? meter[0];
  }
  return out;
}

export interface MeasureBeat {
  beat: number;
  accent: boolean;
  /** Which beat of its bar this is, from 0 (a pickup's beats count back from the bar's end). */
  inBar?: number;
  /** Beats in the bar's meter. */
  barBeats?: number;
}

/**
 * Metronome pulse positions for a written measure grid. A fractional pickup
 * counts backwards from the following downbeat, so a 3.5-beat anacrusis in
 * 4/4 clicks at .5, 1.5 and 2.5 before accenting the body at 3.5.
 */
export function measureBeatGrid(
  measures: Pick<IRealMeasure, 'timeSig' | 'pickupBeats'>[],
  defaultTimeSig: [number, number],
): MeasureBeat[] {
  const out: MeasureBeat[] = [];
  let meter = defaultTimeSig;
  let start = 0;
  for (const measure of measures) {
    meter = measure.timeSig ?? meter;
    const length = measure.pickupBeats ?? meter[0];
    const pickup = measure.pickupBeats != null;
    const fraction = length - Math.floor(length);
    const first = pickup && fraction > 1e-9 ? fraction : 0;
    for (let offset = first; offset < length - 1e-9; offset += 1) {
      out.push({
        beat: start + offset,
        accent: !pickup && offset < 1e-9,
        inBar: Math.round(meter[0] - length + offset),
        barBeats: meter[0],
      });
    }
    start += length;
  }
  return out;
}
